import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import TableToolbar from './TableToolbar';
import SubTabs from './SubTabs';

type BlendComponent = {
  id: number;
  blend_id: number;
  roast_batch_id: number;
  product_id: number;
  percentage: number | null;
  weight: number;
  batch_code: string | null;
  roast_date: string;
  batch_product_name: string;
  batch_product_sku: string;
};

type PostRoastBlend = {
  id: number;
  name: string;
  ratio_type: 'percentage' | 'weight';
  total_output_weight: number;
  output_product_id: number;
  notes?: string | null;
  created_at: string;
  output_product_name: string;
  output_product_sku: string;
  components: BlendComponent[];
};

type RoastBatchSummary = {
  id: number;
  batch_code: string | null;
  product_id: number;
  product_name: string;
  roast_date: string;
  green_weight: number;
  roasted_weight: number;
};

type ProductSummary = {
  id: number;
  name: string;
  sku: string;
  is_green_coffee: number;
};

type FormValues = {
  name: string;
  output_product_id: number;
  total_output_weight?: number;
  notes?: string;
};

type ComponentRow = {
  id: string;
  roast_batch_id: string;
  value: string;
};

const createRowId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

const toOptional = (input?: string) =>
  input && input.trim().length > 0 ? input.trim() : undefined;

const PostRoastBlendsPage = () => {
  const queryClient = useQueryClient();
  const [ratioMode, setRatioMode] = useState<'percentage' | 'weight'>('percentage');
  const [componentRows, setComponentRows] = useState<ComponentRow[]>([
    { id: createRowId(), roast_batch_id: '', value: '' }
  ]);
  const [componentError, setComponentError] = useState<string | null>(null);

  const productsQuery = useQuery({
    queryKey: ['products'],
    queryFn: () => api.get<ProductSummary[]>('/api/products')
  });

  const roastBatchesQuery = useQuery({
    queryKey: ['roast-batches'],
    queryFn: () => api.get<RoastBatchSummary[]>('/api/roast-batches')
  });

  const blendsQuery = useQuery({
    queryKey: ['post-roast-blends'],
    queryFn: () => api.get<PostRoastBlend[]>('/api/post-roast-blends')
  });

  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<'name' | 'output' | 'total' | 'created'>('created');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [activeTab, setActiveTab] = useState<'history' | 'create'>('history');
  const sortedBlends = useMemo(() => {
    const rows = (blendsQuery.data ?? []).filter((b) => {
      if (!filter) return true;
      const f = filter.toLowerCase();
      const hay = [b.name, b.output_product_name, String(b.total_output_weight), b.ratio_type, b.notes ?? '', b.created_at]
        .join(' ')
        .toLowerCase();
      return hay.includes(f);
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    return rows.sort((a, b) => {
      let va: string | number = '';
      let vb: string | number = '';
      if (sortKey === 'name') {
        va = a.name; vb = b.name;
      } else if (sortKey === 'output') {
        va = a.output_product_name; vb = b.output_product_name;
      } else if (sortKey === 'total') {
        va = a.total_output_weight; vb = b.total_output_weight;
      } else {
        va = a.created_at; vb = b.created_at;
      }
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [blendsQuery.data, filter, sortKey, sortDir]);

  const form = useForm<FormValues>({
    defaultValues: {
      name: '',
      output_product_id: undefined,
      total_output_weight: undefined,
      notes: ''
    }
  });

  const nonGreenProducts = useMemo(
    () =>
      (productsQuery.data ?? []).filter((product) => (product.is_green_coffee ?? 0) === 0),
    [productsQuery.data]
  );

  const roastBatchOptions = useMemo(
    () =>
      (roastBatchesQuery.data ?? []).map((batch) => ({
        ...batch,
        label: `${batch.batch_code ? batch.batch_code : `Batch ${batch.id}`} - ${batch.product_name} - ${batch.roasted_weight.toFixed(2)} kg`
      })),
    [roastBatchesQuery.data]
  );

  const totalPercentage = useMemo(
    () =>
      componentRows.reduce((sum, row) => {
        const value = Number(row.value);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0),
    [componentRows]
  );

  const totalWeight = useMemo(
    () =>
      componentRows.reduce((sum, row) => {
        const value = Number(row.value);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0),
    [componentRows]
  );

  const createBlend = useMutation({
    mutationFn: (values: {
      name: string;
      ratio_type: 'percentage' | 'weight';
      output_product_id: number;
      total_output_weight?: number;
      notes?: string;
      components: Array<{ roast_batch_id: number; value: number }>;
    }) => api.post<PostRoastBlend>('/api/post-roast-blends', values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['post-roast-blends'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
      queryClient.invalidateQueries({ queryKey: ['roast-batches'] });
      setComponentRows([{ id: createRowId(), roast_batch_id: '', value: '' }]);
      setRatioMode('percentage');
      setComponentError(null);
      form.reset({ name: '', notes: '', output_product_id: undefined, total_output_weight: undefined });
    }
  });

  const handleComponentChange = (id: string, field: 'roast_batch_id' | 'value', value: string) => {
    setComponentRows((previous) =>
      previous.map((row) =>
        row.id === id
          ? {
              ...row,
              [field]: value
            }
          : row
      )
    );
  };

  const handleRemoveComponent = (id: string) => {
    setComponentRows((previous) => (previous.length === 1 ? previous : previous.filter((row) => row.id !== id)));
  };

  const handleAddComponent = () => {
    setComponentRows((previous) => [...previous, { id: createRowId(), roast_batch_id: '', value: '' }]);
  };

  const onSubmit = (values: FormValues) => {
    const preparedComponents = componentRows
      .map((row) => ({
        roast_batch_id: row.roast_batch_id ? Number(row.roast_batch_id) : NaN,
        value: row.value ? Number(row.value) : NaN
      }))
      .filter((item) => Number.isFinite(item.roast_batch_id) && Number.isFinite(item.value) && item.value > 0);

    if (preparedComponents.length === 0) {
      setComponentError('Add at least one roast batch with a positive ratio.');
      return;
    }

    if (new Set(preparedComponents.map((item) => item.roast_batch_id)).size !== preparedComponents.length) {
      setComponentError('Each roast batch can only be selected once.');
      return;
    }

    if (ratioMode === 'percentage') {
      if (!values.total_output_weight || values.total_output_weight <= 0) {
        form.setError('total_output_weight', {
          type: 'manual',
          message: 'Provide the total output weight for percentage blends.'
        });
        setComponentError('Provide the total output weight for percentage blends.');
        return;
      }
      if (Math.abs(totalPercentage - 100) > 0.1) {
        setComponentError('Component percentages must total 100%.');
        return;
      }
    } else if (totalWeight <= 0) {
      setComponentError('Component weights must sum to a positive value.');
      return;
    }

    form.clearErrors('total_output_weight');
    setComponentError(null);

    createBlend.mutate({
      name: values.name.trim(),
      ratio_type: ratioMode,
      output_product_id: Number(values.output_product_id),
      total_output_weight: ratioMode === 'percentage' ? Number(values.total_output_weight) : undefined,
      notes: toOptional(values.notes),
      components: preparedComponents.map((component) => ({
        roast_batch_id: Number(component.roast_batch_id),
        value: Number(component.value)
      }))
    });
  };

  return (
    <div>
      <h2>Post-Roast Blends</h2>
      <p style={{ color: '#64748b' }}>
        Define blends using roasted batches, track ratio formulas, and automatically adjust roasted inventory when a
        blend is created.
      </p>

      <SubTabs
        activeTab={activeTab}
        onChange={setActiveTab}
        tabs={[
          { id: 'history', label: 'Blend History', badge: sortedBlends.length },
          { id: 'create', label: 'Create Blend' }
        ]}
      />

      {activeTab === 'create' ? (
      <div className="card" style={{ marginTop: 16 }}>
        <h3>Create blend</h3>
        <form className="form-grid" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="form-group">
            <label>Name</label>
            <input placeholder="Morning House Blend" {...form.register('name', { required: true })} />
          </div>
          <div className="form-group">
            <label>Output product</label>
            <select {...form.register('output_product_id', { valueAsNumber: true, required: true })}>
              <option value="">Select product</option>
              {nonGreenProducts.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name} ({product.sku})
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ gridColumn: 'span 2' }}>
            <label>Ratio mode</label>
            <div style={{ display: 'flex', gap: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="radio"
                  name="ratio-mode"
                  checked={ratioMode === 'percentage'}
                  onChange={() => setRatioMode('percentage')}
                />
                Percentage
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="radio"
                  name="ratio-mode"
                  checked={ratioMode === 'weight'}
                  onChange={() => setRatioMode('weight')}
                />
                Weight (kg)
              </label>
            </div>
          </div>
          {ratioMode === 'percentage' && (
            <div className="form-group">
              <label>Total output weight (kg)</label>
              <input
                type="number"
                step="0.01"
                {...form.register('total_output_weight', { valueAsNumber: true })}
              />
            </div>
          )}
          <div className="form-group" style={{ gridColumn: 'span 2' }}>
            <label>Components</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {componentRows.map((row) => (
                <div
                  key={row.id}
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}
                >
                  <select
                    value={row.roast_batch_id}
                    onChange={(event) => handleComponentChange(row.id, 'roast_batch_id', event.target.value)}
                    style={{ minWidth: 220 }}
                  >
                    <option value="">Select roast batch</option>
                    {roastBatchOptions.map((batch) => (
                      <option key={batch.id} value={batch.id}>
                        {batch.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    placeholder={ratioMode === 'percentage' ? 'Percentage %' : 'Weight kg'}
                    value={row.value}
                    onChange={(event) => handleComponentChange(row.id, 'value', event.target.value)}
                    style={{ width: 140 }}
                  />
                  <button type="button" className="btn" onClick={() => handleRemoveComponent(row.id)}>
                    Remove
                  </button>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <button type="button" className="btn" onClick={handleAddComponent}>
                  Add component
                </button>
                <span style={{ color: '#64748b', fontSize: 12 }}>
                  {ratioMode === 'percentage'
                    ? `Total: ${totalPercentage.toFixed(2)}%`
                    : `Total weight: ${totalWeight.toFixed(2)} kg`}
                </span>
              </div>
              {componentError && <p style={{ color: '#dc2626', fontSize: 12 }}>{componentError}</p>}
            </div>
          </div>
          <div className="form-group" style={{ gridColumn: 'span 2' }}>
            <label>Notes</label>
            <textarea rows={2} {...form.register('notes')} />
          </div>
          <button className="btn primary" type="submit" disabled={createBlend.isPending}>
            {createBlend.isPending ? 'Saving...' : 'Create blend'}
          </button>
        </form>
      </div>
      ) : null}

      {activeTab === 'history' ? (
      <section style={{ marginTop: 8 }}>
        <h3>Blend history</h3>
        {blendsQuery.isLoading && <p style={{ color: '#64748b' }}>Loading blends...</p>}
        {!blendsQuery.isLoading && blendsQuery.data && blendsQuery.data.length === 0 && (
          <p style={{ color: '#64748b' }}>No blends recorded yet.</p>
        )}
        <TableToolbar value={filter} onChange={setFilter} placeholder="Filter blends…" />
        <div className="table-wrapper" style={{ marginTop: 16 }}>
          <table>
            <thead>
              <tr>
                <th onClick={() => { if (sortKey === 'name') setSortDir((d)=> d==='asc'?'desc':'asc'); setSortKey('name'); }} style={{ cursor: 'pointer' }}>
                  Name{sortKey === 'name' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th onClick={() => { if (sortKey === 'output') setSortDir((d)=> d==='asc'?'desc':'asc'); setSortKey('output'); }} style={{ cursor: 'pointer' }}>
                  Output{sortKey === 'output' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th onClick={() => { if (sortKey === 'total') setSortDir((d)=> d==='asc'?'desc':'asc'); setSortKey('total'); }} style={{ cursor: 'pointer' }}>
                  Total weight{sortKey === 'total' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th>Ratio type</th>
                <th>Components</th>
                <th onClick={() => { if (sortKey === 'created') setSortDir((d)=> d==='asc'?'desc':'asc'); setSortKey('created'); }} style={{ cursor: 'pointer' }}>
                  Created{sortKey === 'created' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedBlends.map((blend) => (
                <tr key={blend.id}>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>{blend.name}</span>
                      {blend.notes && <span style={{ color: '#94a3b8', fontSize: 12 }}>{blend.notes}</span>}
                    </div>
                  </td>
                  <td>
                    {blend.output_product_name}
                    <span style={{ color: '#94a3b8', fontSize: 12 }}> ({blend.output_product_sku})</span>
                  </td>
                  <td>{blend.total_output_weight.toFixed(2)} kg</td>
                  <td>{blend.ratio_type === 'percentage' ? 'Percentage' : 'Weight'}</td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {blend.components.map((component) => (
                        <span key={component.id} className="badge">
                          {component.batch_code ? component.batch_code : `Batch ${component.roast_batch_id}`} - {component.batch_product_name} - {component.weight.toFixed(2)} kg
                          {component.percentage !== null && ` (${component.percentage.toFixed(2)}%)`}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>{new Date(blend.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      ) : null}
    </div>
  );
};

export default PostRoastBlendsPage;
